import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import ClaimButton from "@/components/ClaimButton";
import {
  getInstitution,
  followInstitution,
  submitInstitutionVerification,
} from "@/api/institutions";
import {
  listOpportunitiesForInstitution,
  signupForOpportunity,
  type VolunteerOpportunity,
} from "@/api/volunteers";

export default function InstitutionDetail() {
  const { idOrSlug } = useParams<{ idOrSlug: string }>();
  const qc = useQueryClient();
  const [actionFeedback, setActionFeedback] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["institution", idOrSlug],
    queryFn: () => getInstitution(idOrSlug!),
    enabled: !!idOrSlug,
  });

  const inst = data?.data;
  const isMember = !!inst?.viewerMembership;

  const { data: opps } = useQuery({
    queryKey: ["institution-opportunities", inst?.id],
    queryFn: () =>
      listOpportunitiesForInstitution(inst!.id, { upcomingOnly: true }),
    enabled: !!inst?.id,
  });

  async function handleFollow() {
    if (!inst) return;
    try {
      await followInstitution(inst.id);
      setActionFeedback("Followed");
      qc.invalidateQueries({ queryKey: ["institution", idOrSlug] });
    } catch (err) {
      setActionFeedback(err instanceof Error ? err.message : "Failed");
    }
  }

  async function handleSubmitVerification() {
    if (!inst) return;
    try {
      await submitInstitutionVerification(inst.id);
      setActionFeedback("Verification submitted");
      qc.invalidateQueries({ queryKey: ["institution", idOrSlug] });
    } catch (err) {
      setActionFeedback(err instanceof Error ? err.message : "Failed");
    }
  }

  if (isLoading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (!inst) return <p className="text-sm text-gray-500">Not found.</p>;

  return (
    <div className="space-y-6">
      <PageHeader
        title={inst.name}
        subtitle={
          inst.tagline ?? `${inst.institutionType} · ${inst.city}, ${inst.state}`
        }
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Institutions", href: "/lab/institutions" },
          { label: inst.name },
        ]}
        actions={
          <>
            <Link
              to={`/lab/institutions/${inst.id}/manage`}
              className="text-sm text-primary-600 hover:underline self-center mr-2"
            >
              manage →
            </Link>
            <Button variant="secondary" size="sm" onClick={handleFollow}>
              Follow
            </Button>
            {isMember && inst.verificationStatus === "draft" && (
              <Button size="sm" onClick={handleSubmitVerification}>
                Submit for verification
              </Button>
            )}
          </>
        }
      />

      {actionFeedback && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm px-3 py-2 rounded">
          {actionFeedback}
        </div>
      )}

      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Run this institution?</h2>
        <ClaimButton entityType="institution" entityId={inst.id} />
      </section>

      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex items-center gap-3 flex-wrap mb-3">
          <Badge
            variant={
              inst.verificationStatus === "approved"
                ? "green"
                : inst.verificationStatus === "pending"
                  ? "yellow"
                  : "gray"
            }
          >
            {inst.verificationStatus}
          </Badge>
          {isMember && (
            <Badge variant="purple">
              you&apos;re {inst.viewerMembership?.role}
            </Badge>
          )}
        </div>
        {inst.description && (
          <p className="text-sm text-gray-700 mb-4">{inst.description}</p>
        )}
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Stat label="Followers" value={inst.followerCount} />
          <Stat label="Events" value={inst.eventCount} />
          <Stat label="Attendance" value={inst.totalAttendance} />
          <Stat label="Volunteers" value={inst.volunteerCount} />
        </dl>
      </section>

      {/* Volunteer opportunities at this institution */}
      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Volunteer opportunities
          </h2>
          <Link
            to="/lab/volunteers"
            className="text-sm text-primary-600 hover:underline"
          >
            My history →
          </Link>
        </div>
        {!opps || opps.data.items.length === 0 ? (
          <p className="text-sm text-gray-500">No open opportunities.</p>
        ) : (
          <ul className="space-y-3">
            {opps.data.items.map((o) => (
              <OpportunityRow
                key={o.id}
                o={o}
                onSignedUp={() => {
                  setActionFeedback(`Signed up for "${o.title}"`);
                  qc.invalidateQueries({
                    queryKey: ["institution-opportunities", inst.id],
                  });
                }}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function OpportunityRow({
  o,
  onSignedUp,
}: {
  o: VolunteerOpportunity;
  onSignedUp: () => void;
}) {
  const [busy, setBusy] = useState(false);
  async function signup() {
    setBusy(true);
    try {
      await signupForOpportunity(o.id);
      onSignedUp();
    } finally {
      setBusy(false);
    }
  }
  return (
    <li className="border border-gray-200 rounded-xl px-4 py-3 flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-900">{o.title}</span>
          <Badge variant="gray">{o.category}</Badge>
          <Badge variant={o.status === "open" ? "green" : "gray"}>{o.status}</Badge>
        </div>
        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{o.description}</p>
        <div className="text-xs text-gray-500 mt-1">
          {o.spotsFilled}/{o.spotsAvailable} spots filled
          {o.date && ` · ${new Date(o.date).toLocaleDateString()}`}
          {o.yashPoints != null && ` · ${o.yashPoints} yash`}
        </div>
      </div>
      <Button
        size="sm"
        disabled={o.status !== "open" || o.spotsFilled >= o.spotsAvailable}
        isLoading={busy}
        onClick={signup}
      >
        Sign up
      </Button>
    </li>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-semibold text-gray-900">{value}</div>
    </div>
  );
}
